import { syncLoginLogoPreload } from 'utils/login-logo-preload'

export const BOOT_SOURCE_TIMEOUT_MS = 8000

type HeaderFlags = { show?: boolean; showLogo?: boolean }

type AssetTier = { url?: string; darkUrl?: string }

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  value !== null && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : undefined

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined

const resolveAssetUrl = (
  tiers: (AssetTier | undefined)[],
  isDark: boolean
): string | undefined => {
  for (const tier of tiers) {
    const url = (isDark && tier?.darkUrl) || tier?.url

    if (url) return url
  }

  return undefined
}

export const isLoginDocument = (pathname: string) =>
  pathname === '/login' || pathname.startsWith('/login/')

export const isBootAppearanceDark = (
  root: Element = document.documentElement
) => root.classList.contains('dark')

export const readLoginHeaderLogo = (
  config: Record<string, unknown>
): string | undefined => {
  const header = asRecord(asRecord(asRecord(config.config)?.login)?.header) as
    | HeaderFlags
    | undefined

  if (header?.show === false || header?.showLogo === false) return undefined

  return asString(asRecord(config.images)?.loginHeaderLogo)
}

const readDesignLogoTier = (theme: unknown): AssetTier | undefined => {
  const assets = asRecord(
    asRecord(asRecord(theme)?.design)?.spark_theme
  )?.assets

  const record = asRecord(assets)

  if (!record) return undefined

  return {
    url: asString(record.logoUrl),
    darkUrl: asString(record.logoDarkUrl)
  }
}

const readPageLogoImage = (page: unknown) =>
  asRecord(asRecord(asRecord(page)?.content)?.logoImage)

const readPageLogoTier = (
  pages: unknown,
  pathname: string
): AssetTier | undefined => {
  if (!Array.isArray(pages)) return undefined

  const slug = pathname.replace(/\/+$/, '').split('/').pop() || ''
  const selected = pages.find((page) => asRecord(page)?.slug === slug)
  const selectedLogo = readPageLogoImage(selected)
  const logo =
    selectedLogo ||
    readPageLogoImage(
      pages.find((page) => {
        const image = readPageLogoImage(page)

        return asString(image?.url) || asString(image?.darkUrl)
      })
    )

  if (!logo) return undefined

  return { url: asString(logo.url), darkUrl: asString(logo.darkUrl) }
}

export const readLoginPageLogo = (
  pages: unknown,
  pathname: string,
  isDark: boolean
): string | undefined =>
  resolveAssetUrl([readPageLogoTier(pages, pathname)], isDark)

export const readLoginThemeLogo = (
  theme: unknown,
  isDark: boolean
): string | undefined => resolveAssetUrl([readDesignLogoTier(theme)], isDark)

export const waitForBootSource = <T>(
  promise: Promise<T> | undefined,
  timeoutMs = BOOT_SOURCE_TIMEOUT_MS
): Promise<T | undefined> => {
  if (!promise) return Promise.resolve(undefined)

  return new Promise((resolve) => {
    const timer = window.setTimeout(() => resolve(undefined), timeoutMs)

    promise.then(
      (value) => {
        window.clearTimeout(timer)
        resolve(value)
      },
      () => {
        window.clearTimeout(timer)
        resolve(undefined)
      }
    )
  })
}

export const preloadLoginLogo = (url: string) => {
  syncLoginLogoPreload(url)
}
